import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Tab, TabList, TabPanel, Tabs } from "@similarweb/ui-components";

import { tableActionsCreator } from "app/actions/tableActions";
import { keywordGroupsTransforms } from "app/services/keyword-groups";
import type { Filter } from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";
import { EKWGeneratorTableTabs } from "app/common/keyword-research/keyword-generator/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import {
    TABLE_SELECTION_KEY,
    TABLE_SELECTION_PROPERTY,
    WIDGET_TITLE_KEY,
    WIDGET_TOOLTIP_KEY,
} from "./keywordIdeasConsts";
import { KeywordIdeasTable } from "./KeywordIdeasTable";
import { EKeywordIdeasTabs, ETabIndexToTabName } from "./keywordIdeasTypes";
import { getUpdatedSubtitleFilters } from "./keywordIdeasUtils";

const KeywordIdeasWrapper = styled.div<{ isKeywordGroup: boolean }>`
    width: 100%;
    .swReactTable-container {
        border-top: unset;
    }
    .swTable-headerCell {
        background-color: ${colorsPalettes.carbon[0]};
        border-right: unset;
        height: 40px;
    }
    .swTable-cell {
        &:last-child {
            border-bottom: unset;
        }
    }
    .keyword-column {
        .swTable-headerCell,
        .keyword-cell {
            padding-left: ${({ isKeywordGroup }) => (!isKeywordGroup ? "24px" : 0)};
        }
    }
`;

interface IKeywordIdeas {
    subtitleFilters: Filter[];
}

export const KeywordIdeas: FC<IKeywordIdeas> = ({ subtitleFilters }) => {
    const dispatch = useDispatch();
    const { params } = useCurrentStateAndParams();
    const { keyword, duration, keywordIdeasTab: selectTab } = params;

    const { clearAllSelectedRows } = tableActionsCreator(TABLE_SELECTION_KEY, TABLE_SELECTION_PROPERTY);
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keyword);

    const subtitleUpdatedForDurationAndWebSource = getUpdatedSubtitleFilters(subtitleFilters, duration);

    const handleTabSelect = (index: number) => {
        dispatch(clearAllSelectedRows());
        const tab = ETabIndexToTabName[index];
        proNavigator.updateQueryParams({ keywordIdeasTab: tab }, { reload: false });
    };

    return (
        <KeywordIdeasWrapper
            data-automation="KeywordsIdeas"
            isKeywordGroup={isKeywordGroup}
        >
            <WidgetContainer
                title={translate(WIDGET_TITLE_KEY)}
                tooltip={translate(WIDGET_TOOLTIP_KEY)}
                subtitleFilters={subtitleUpdatedForDurationAndWebSource ?? []}
                minHeight={260}
            >
                <Tabs
                    onSelect={handleTabSelect}
                    selectedIndex={ETabIndexToTabName[selectTab as keyof typeof ETabIndexToTabName]}
                >
                    <TabList>
                        <Tab id={EKeywordIdeasTabs.RELATED}>
                            {translate("keyword.analysis.overview.page.v2.keyword_ideas.tab.related")}
                        </Tab>
                        <Tab id={EKeywordIdeasTabs.TRENDING}>
                            {translate("keyword.analysis.overview.page.v2.keyword_ideas.tab.trending")}
                        </Tab>
                        <Tab id={EKeywordIdeasTabs.QUESTIONS}>
                            {translate("keyword.analysis.overview.page.v2.keyword_ideas.tab.questions")}
                        </Tab>
                    </TabList>
                    <TabPanel>
                        <KeywordIdeasTable
                            hasChange={true}
                            tabName={EKWGeneratorTableTabs.relatedKeywords}
                        />
                    </TabPanel>
                    <TabPanel>
                        <KeywordIdeasTable
                            hasChange={false}
                            tabName={EKWGeneratorTableTabs.trending}
                        />
                    </TabPanel>
                    <TabPanel>
                        <KeywordIdeasTable
                            hasChange={false}
                            tabName={EKWGeneratorTableTabs.questions}
                        />
                    </TabPanel>
                </Tabs>
            </WidgetContainer>
        </KeywordIdeasWrapper>
    );
};
